/**
 * The top of a page that is not the home page.
 *
 * Its real job is the index under the heading. A reader who has landed on a
 * seven-band page needs to know, before scrolling, that the band they came for
 * is on it — and the bands are long enough that finding out by scrolling costs
 * more than most readers will spend. The list is the page's own structure, not
 * a navigation invention: every entry is a section that is genuinely there, and
 * `SECTION_LABEL` will not compile for a section with no label written.
 *
 * The eyebrow copy is reused for the index rather than a second set of strings.
 * Those words are already the shortest true name each section has, and two
 * catalogues of names for the same nine things is two places for them to drift
 * apart in two languages.
 */
import { Reveal } from '../kit'
import { useT } from '../i18n/context'
import type { MessageKey } from '../i18n'

/** Every section an index may point at, with the key naming it. The ids are the
 *  ones `Sections.tsx` renders; a typo here is a link to nowhere, so they are
 *  written once and the type closes the set. */
const SECTION_LABEL = {
  pools: 'pools.eyebrow',
  decision: 'decision.eyebrow',
  execution: 'exec.eyebrow',
  evidence: 'evidence.eyebrow',
  approval: 'approval.eyebrow',
  continuity: 'continuity.eyebrow',
  fleet: 'fleet.eyebrow',
  security: 'security.eyebrow',
  install: 'install.eyebrow',
} as const satisfies Record<string, MessageKey>

export type SectionId = keyof typeof SECTION_LABEL

export function PageHeader({
  eyebrow,
  title,
  sub,
  toc,
}: {
  eyebrow: MessageKey
  title: MessageKey
  sub: MessageKey
  toc: readonly SectionId[]
}) {
  const t = useT()

  return (
    <section className="kl-section kl-pagehead" aria-labelledby="page-title">
      <div className="kl-inner">
        <Reveal className="kl-head">
          <p className="k-eyebrow">
            <span className="k-label">{t(eyebrow)}</span>
          </p>
          <h1 id="page-title" className="kl-pagehead__title">
            {t(title)}
          </h1>
          <p className="kl-sub kl-pagehead__sub">{t(sub)}</p>
        </Reveal>

        {/* A nav rather than a bare list: it is a set of links to somewhere
            else on the document, which is what the landmark means, and it gives
            a screen-reader user a way to jump the whole index. */}
        <nav className="kl-toc" aria-label={t('ui.onThisPage')}>
          <ol className="kl-toc__list">
            {toc.map((id, i) => (
              <li key={id} className="kl-toc__item">
                <a className="kl-toc__link" href={`#${id}`}>
                  <span className="kl-toc__num k-mono">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="kl-toc__word">{t(SECTION_LABEL[id])}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </div>
    </section>
  )
}
